import React from "react";
import { Form } from "react-bootstrap";
import { every, filter, includes, map, some, transform } from "lodash";

import IconHeader from "components/IconHeader";
import IconSubheader from "components/IconSubheader";
import Button from "components/Button";
import Alert from "components/Alert";

import {
  allPayrollIntegrations,
  autoApproveJoinRequestsId,
  companionPlanId,
  daysBeforeDebitId,
  disableReferralsId,
  einVerificationId,
  gustoId,
  iSolvedPayrollIntegrationId,
  paylocityIntegrationId,
  primaryPlanId,
  statePlanId,
  uniqueInviteCodesId,
  useDbaId,
  virtualBankId,
  viventiumPayrollIntegrationId,
  useGroupNameId,
  ukgPayrollIntegrationId,
} from "statics/featureIds";

const features = [
  {
    label: "Primary Plan",
    id: primaryPlanId,
  },
  {
    label: "Companion Plan",
    id: companionPlanId,
  },
  {
    label: "State Plan",
    id: statePlanId,
  },
  {
    label: "Allow option to expire group invites",
    id: uniqueInviteCodesId,
  },
  {
    label: "Fund via 3rd party ACH/WIRE",
    id: virtualBankId,
  },
  {
    label: "Validate employees using EIN",
    id: einVerificationId,
  },
  {
    label: "Allow option to choose group days before debit",
    id: daysBeforeDebitId,
  },
  {
    label: "Show group name in place of company name",
    id: useGroupNameId,
  },
  {
    label: "Show DBA in place of company name",
    id: useDbaId,
  },
  {
    label: "Disable Referrals",
    id: disableReferralsId,
  },
  {
    label: "Enable Gusto for payroll roster and deduction syncing",
    id: gustoId,
  },
  {
    label: "Enable Viventium for payroll roster and deduction syncing",
    id: viventiumPayrollIntegrationId,
  },
  {
    label: "Enable iSolved for payroll roster and deduction syncing",
    id: iSolvedPayrollIntegrationId,
  },
  {
    label: "Enable Auto-Approval of Employer Link Requests",
    id: autoApproveJoinRequestsId,
  },
  {
    label: "Enable Paylocity for payroll roster and deduction syncing",
    id: paylocityIntegrationId,
  },
  {
    label: "Enable UKG for payroll roster and deduction syncing",
    id: ukgPayrollIntegrationId,
  },
];

interface Props {
  // PropTypes had only onChange/enabledProductFeatures/isFetching/error;
  // the parent (EmployerInfo.tsx) also passes companyId, which this component
  // never reads — declared optional so the parent typechecks.
  companyId?: string;
  // PropTypes `.func`; parent's handler is (enabledFeatures: number[]) => void
  // and `enabledFeatures` here is built by pushing `+key` (numbers).
  onChange?: (enabledFeatures: number[]) => void;
  // PropTypes `.object`; parent passes Record<string, boolean> keyed by feature id.
  enabledProductFeatures?: Record<string, boolean>;
  isFetching?: boolean;
  error?: string;
}

interface State {
  // Seeded from props.enabledProductFeatures (may be undefined) and written by
  // setState with dynamic feature-id keys.
  enabledProductFeatures?: Record<string, boolean>;
}

export default class ProductFeatures extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      enabledProductFeatures: props.enabledProductFeatures,
    };
  }

  _handleProductFeatureChange = ({
    target: { name, checked },
  }: React.ChangeEvent<HTMLInputElement>) => {
    const enabledProductFeatures = {
      ...this.state.enabledProductFeatures,
      [name]: checked,
    };

    // can only have one payroll integration enabled at a time
    const isPayrollIntegration = includes(allPayrollIntegrations, +name);
    if (isPayrollIntegration && checked) {
      const featuresToDisable = filter(
        allPayrollIntegrations,
        (id) => id !== +name
      );
      featuresToDisable.forEach((featureToDisable) => {
        enabledProductFeatures[featureToDisable] = false;
      });
    }

    this.setState({
      enabledProductFeatures,
    });
  };

  _updateProductFeatures = () => {
    const enabledFeatures = transform(
      // state value is optional; lodash.transform's overloads don't accept
      // undefined. The `!` is compile-time only — runtime is unchanged
      // (transform(undefined, ...) simply returns the accumulator).
      this.state.enabledProductFeatures!,
      (acc: number[], val, key) => {
        if (val) {
          acc.push(+key);
        }
        return acc;
      },
      [] as number[]
    );
    // onChange is optional in Props; the original invokes it unconditionally
    // (throws if absent) — preserve that with a non-null assertion.
    this.props.onChange!(enabledFeatures);
  };

  _getFeatureCheckboxes = () => {
    return map(features, (feature) => {
      return (
        <Form.Check
          type="checkbox"
          // feature.id is a number; React stringifies it at render. Form.Check's
          // id/name expect string — cast to keep the original numeric runtime value.
          id={feature.id as unknown as string}
          key={feature.id}
          checked={this.state.enabledProductFeatures![feature.id]}
          name={feature.id as unknown as string}
          disabled={false}
          label={feature.label}
          onChange={this._handleProductFeatureChange}
        />
      );
    });
  };

  render() {
    /*
     btn should be disabled if no changes were made, can't directly match objects because
     when you click and unclick a check box it gets added to the state as false so have to 
     use custom logic to check for that by ignore false values if the feature flag wasn't already enabled
    */
    const btnDisabled = every(this.state.enabledProductFeatures, (val, key) => {
      // enabledProductFeatures is optional in Props but the original indexes it
      // directly (throws if absent) — preserve with a non-null assertion.
      if (this.props.enabledProductFeatures![key]) {
        return val === this.props.enabledProductFeatures![key];
      } else {
        return !val;
      }
    });

    const hasASelectedPlanType = some(
      this.state.enabledProductFeatures,
      (isChecked, key) => {
        // key is the (string) object key; the *Id constants are numbers. The
        // original relies on loose `==` to coerce string key -> number. Cast the
        // numeric ids to string operands so the loose comparison typechecks
        // without altering the runtime value (cast is erased at compile time).
        if (
          key == (primaryPlanId as unknown as string) ||
          key == (companionPlanId as unknown as string) ||
          key == (statePlanId as unknown as string)
        ) {
          return isChecked;
        }
        return false;
      }
    );

    return (
      <div className="action-box">
        <Form.Group className="mb-3" controlId="formProductFeatures">
          <div className="action-header">
            <span>
              <IconHeader variant="labelHeader" headerText="Product Features" />
              <IconSubheader subheader="Enable product features for a company" />
            </span>

            <div>
              <Button
                size="sm"
                disabled={btnDisabled || !hasASelectedPlanType}
                onClick={this._updateProductFeatures}
                btnLabel="Update"
                name="submit"
                withArrow
                loading={this.props.isFetching}
              />
            </div>
          </div>
          <div className="action-body">{this._getFeatureCheckboxes()}</div>
        </Form.Group>
        {this.props.error && <Alert msg={this.props.error} type="error" />}
      </div>
    );
  }
}
